import React from "react";
import { styled } from "@hiyllo/ux/styled";
import { Button } from "@hiyllo/ux/button";
import { Structure } from "@hiyllo/ux/structure";
import { Typography } from "@hiyllo/ux/typography";
import { MouseInteraction } from "@hiyllo/ux/animation";
import { Table, TableRow, TableCell } from "@hiyllo/ux/table";
import { Input } from "@hiyllo/ux/input";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowRight, faBan, faBrowser, faCheck, faGlobe, faHammer, faLaptopMobile, faLink, faMobile, faPlus, faServer } from "@fortawesome/pro-light-svg-icons";
import { useTheme } from "@hiyllo/ux/theme";
import axios from "axios";

const IS_MOBILE = window.innerHeight > window.innerWidth;

type ContractType = ({
    kind: "website";
    /**
     * Whether client requires support setting up 
     */
    dnsSetupNeeded?: boolean;
} | {
    kind: "mobile-app";
} | {
    kind: "full-stack-app";
} | {
    kind: "misc";
}) & {
    contact: string;
    description: string;
} 

const Container = styled("div", ({$theme}) => ({
    padding: 50,
    backgroundColor: $theme.background1,
    height: "calc(100% - 100px)",
    overflowY: "auto",
    color: $theme.foreground,
    fontFamily: "hiyllo"
}));

const Title = styled("div", ({$theme}) => ({
    fontFamily: "hiyllo",
    fontSize: 37.5,
    fontWeight: "bold",
    color: $theme.foreground,
}));

const ComponentPlaceholderContainer = styled<"div", { $selected?: boolean; }>("div", ({$theme, $selected}) => ({
    backgroundColor: $selected ? $theme.background3 : $theme.background2,
    padding: 20,
    borderRadius: 10,
    cursor: "pointer",
    flexGrow: 1,
}));

const ComponentPlaceholderIcon = styled("div", ({$theme}) => ({
    fontSize: 20
}));

const ComponentPlaceholderTitle = styled("div", ({$theme}) => ({
    fontSize: 27.5,
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    gap: 10,
    whiteSpace: "nowrap",
    marginBottom: 5,
}));
const ComponentPlaceholderDescription = styled("div", ({$theme}) => ({
    fontSize: 17.5
}));

const CostDetailsPill = React.memo(function CostDetailsPill({ children }: { children: React.ReactNode }) {
    const $theme = useTheme();

    return (
        <div style={{
            marginTop: 5,
            backgroundColor: $theme.midground1,
            color: $theme.foreground,
            padding: 7.5,
            borderRadius: 5,
            fontSize: 12.5,
            display: "inline-block",
            marginRight: 10
        }}>
            {children}
        </div>
    );
});

/*

    Project:
    - Website
    - Mobile Application
    - Desktop Application
    - Backend Service
    - Turnaround

*/

export const ContractingPage = React.memo(function ContractingPage() {
    const [contract, setContract] = React.useState<Partial<ContractType>>({});

    const [loading, setLoading] = React.useState(false);
    const [success, setSuccess] = React.useState(false);
    const submit = React.useCallback(async () => {
        setLoading(true);

        await fetch("https://www.hiyllo.team/_team/notify-sales?description=" + encodeURIComponent(JSON.stringify(contract, null, 2)), {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
            },
            mode: "no-cors"
        });

        setLoading(false);
        setSuccess(true);
    }, [contract]);

    if(contract.kind == null) {
        return (
        <Container style={{ display: "flex", flexDirection: "column", gap: 20 }}>
            <Structure.Row>
                <img
                    src="https://cdn.hiyllo.net/logo/hiyllo/icon-gradient.png"
                    style={{ height: 40 }}
                    alt=""
                />
                <Title>
                    Contracting
                </Title>
            </Structure.Row>
            <div style={{display:'flex', flexDirection: "row", gap: 10, alignItems: "flex-start", flexWrap: IS_MOBILE ? "wrap" : "nowrap"}}>
                <div>
                    <div><b>Project Definition</b></div>
                    <div>Provide an initial description of your project below, and our product team will reach out with any follow ups. You can meet with us or communicate over email.</div>
                </div>
                <FontAwesomeIcon icon={faArrowRight} />
                <div>
                    <div><b>Work Proposal</b></div>
                    <div>Hiyllo's engineers will review your project needs and provide a flat fixed-rate proposal along with a time estimate range and contract. After signing, you pay 50% of the total project cost</div>
                </div>
                <FontAwesomeIcon icon={faArrowRight} />
                <div>
                    <div><b>Draft Presentation</b></div>
                    <div>After finishing the first draft, Hiyllo will present the completed work for your review. You can provide feedback and request changes.</div>
                </div>
                <FontAwesomeIcon icon={faArrowRight} />
                <div>
                    <div><b>Revisions</b></div>
                    <div>Hiyllo will make any requested revisions depending on the project definition and terms of our contract.</div>
                </div>
                <FontAwesomeIcon icon={faArrowRight} />
                <div>
                    <div><b>Source Code Delivery</b></div>
                    <div>After final payment, Hiyllo will deliver the full source code of the project. If requested in project definition, Hiyllo will also assist with deploying your project</div>
                </div>
            </div>
            <Typography.SubHeader>What kind of project are you looking for?</Typography.SubHeader>
            <div style={IS_MOBILE ? {display: "flex", flexDirection: "column", gap: 20, } : {display: "flex", flexDirection: "row", gap: 20, alignItems:"stretch", flexWrap:"wrap"}}>
                <ComponentPlaceholderContainer style={{flexBasis:"35%"}} onClick={() => setContract({ kind: "website" })}>
                    <div>
                        <ComponentPlaceholderTitle>
                            <FontAwesomeIcon icon={faBrowser}/> Static Website
                        </ComponentPlaceholderTitle>
                        <ComponentPlaceholderDescription>
                            A static informational website that can be used to showcase your business, product, or service. This is a simple website that does not require any complex backend services (like authentication or data persistence).
                            <br/><br/>
                            Static websites can still feature interactive elements, like contact forms, countdown clocks, etc.
                        </ComponentPlaceholderDescription>
                        <FontAwesomeIcon icon={faArrowRight} style={{marginTop:10, fontSize:30}}/>
                    </div>
                </ComponentPlaceholderContainer>
                <ComponentPlaceholderContainer style={{flexBasis:"35%"}} onClick={() => setContract({ kind: "mobile-app" })}>
                    <div>
                        <ComponentPlaceholderTitle>
                            <FontAwesomeIcon icon={faMobile}/> Mobile Application
                        </ComponentPlaceholderTitle>
                        <ComponentPlaceholderDescription>
                            A mobile application for Android and iOS, that either runs using no backend or connectings to an existing backend. This is suitable for applications that require a native mobile experience.
                        </ComponentPlaceholderDescription>
                        <FontAwesomeIcon icon={faArrowRight} style={{marginTop:10, fontSize:30}}/>
                    </div>
                </ComponentPlaceholderContainer>                
                <ComponentPlaceholderContainer style={{flexBasis:"35%"}} onClick={() => setContract({ kind: "full-stack-app" })}>
                    <div>
                        <ComponentPlaceholderTitle>
                            <FontAwesomeIcon icon={faGlobe}/> Full-Stack Application
                        </ComponentPlaceholderTitle>
                        <ComponentPlaceholderDescription>
                            A full-stack application with a web-based frontendn and a backend service. This is suitable for applications that require a web-based user interface and a backend service to handle data storage, authentication, and more.
                        </ComponentPlaceholderDescription>
                        <FontAwesomeIcon icon={faArrowRight} style={{marginTop:10, fontSize:30}}/>
                    </div>
                </ComponentPlaceholderContainer> 
                <ComponentPlaceholderContainer style={{flexBasis:"35%"}} onClick={() => setContract({ kind: "misc" })}>
                    <div>
                        <ComponentPlaceholderTitle>
                            <FontAwesomeIcon icon={faHammer}/> Something Else
                        </ComponentPlaceholderTitle>
                        <ComponentPlaceholderDescription>
                            General engineering services for your project.
                            <br/><br/>
                            <div><b>Examples:</b></div>
                            <div>Build a Discord or Slack chat-bot</div>
                            <br/>
                            <div>Create an AI-model or setup an inference endpoint</div>
                            <br/>
                            <div>Setup an API for use with your own frontend</div>
                            <br/>
                        </ComponentPlaceholderDescription>
                        <FontAwesomeIcon icon={faArrowRight} style={{marginTop:10, fontSize:30}}/>
                    </div>
                </ComponentPlaceholderContainer>                  
            </div>
            <div>
                <b>We primarily work with: </b> JavaScript, TypeScript, ReactJS, NodeJS, and React Native. We can sometimes work with other languages and frameworks upon request, but this may incur additional costs.
            </div>
        </Container>
    );
    }

    return (
        <Container>
            <Structure.Row>
                <img
                    src="https://cdn.hiyllo.net/logo/hiyllo/icon-gradient.png"
                    style={{ height: 40 }}
                    alt=""
                />
                <Title>
                    Contracting
                </Title>
            </Structure.Row>
            <div style={{ height: 10 }}/>
            <Typography.SubHeader>Describe your project in as much detail as possible...</Typography.SubHeader>
            <div style={{height:10}}/>
            <Input
                multiline
                fullWidth
                placeholder="We need a landing page for our new product, a rubber duck that quacks when you squeeze it ..."
                onChangeValue={(value) => {
                    setContract((prev) => ({ ...prev, description: value }));
                }}
                value={contract.description || ""}
            />

            <div style={{height:20}}/>

            <Typography.SubHeader>How would you like us to contact you?</Typography.SubHeader>
            <div style={{height:10}}/>
            <Input
                multiline
                fullWidth
                placeholder="Provide an email address or phone number where we can reach you to discuss your project further."
                onChangeValue={(value) => {
                    setContract((prev) => ({ ...prev, contact: value }));
                }}
                value={contract.contact || ""}
            />

            <div style={{height:20}}/>

            <Button
                onClick={submit}
                isLoading={loading}
                success={success}
                successMessage="Sent!"
                label="Submit Project"
                autoWidth
            />

            <div style={{height:20}}/>

            <div>
                {success ? "Thank you! Your project has been sent to our team. Expect us to reach out shortly" : "When you hit Submit Project above, we will send your project description to our team. We will review it and get back to you with any follow up questions or a proposal."}
            </div>

        </Container>
    );        
});